import { forwardRef } from 'react';
import { ShieldCheck } from 'lucide-react';

export const CERT_WIDTH = 1000;
const SERIF = "'Tinos', 'Times New Roman', Times, serif";
const SANS = "'Arimo', Arial, Helvetica, sans-serif";
const MONO = "'JetBrains Mono', Consolas, 'Courier New', monospace";
const TEAL = '#0891b2';

const formatDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : '-';
};

// description = array of plain strings and { b: 'bold text' } parts
const Description = ({ parts }) => (
  <p className="mx-auto mt-10 max-w-[800px] text-[19px] leading-[2.1] text-[#475569]" data-testid="tw-cert-description">
    {parts.filter(Boolean).map((p, i) => (typeof p === 'string'
      ? <span key={i}>{p}</span>
      : <strong key={i} className="font-bold text-[#334155]">{p.b}</strong>))}
  </p>
);

const Stats = ({ stats }) => (
  <div className="mx-auto mt-16 grid max-w-[860px] gap-6" style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))` }} data-testid="tw-cert-stats">
    {stats.map((s) => (
      <div key={s.label} className="text-center">
        <p className="text-[33px] font-bold leading-none" style={{ color: TEAL }} data-testid={`tw-cert-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{s.value}</p>
        <p className="mx-auto mt-2 max-w-[150px] text-[16px] uppercase leading-[1.35] text-[#475569]">{s.label}</p>
      </div>
    ))}
  </div>
);

const Footer = ({ credentialId, issuedAt }) => (
  <div className="mt-16 grid grid-cols-3 items-end border-t border-[#e5e7eb] pt-7">
    <div className="text-left">
      <p className="break-all text-[17px] font-bold" style={{ color: TEAL, fontFamily: MONO }} data-testid="tw-cert-credential-id">{credentialId}</p>
      <p className="mt-0.5 text-[14px] uppercase text-[#64748b]">Credential ID</p>
    </div>
    <div className="text-center">
      <p className="text-[19px] text-[#1e293b]" data-testid="tw-cert-issued-on">{formatDate(issuedAt)}</p>
      <p className="text-[14px] text-[#475569]">Date Issued</p>
    </div>
    <div className="text-right">
      <div className="mb-2 ml-auto w-[230px] border-t-2 border-[#1e293b]" />
      <p className="text-[19px] text-[#1e293b]">TekWissen Training</p>
      <p className="text-[14px] text-[#475569]" data-testid="tw-cert-signatory-title">L&amp;D</p>
    </div>
  </div>
);

// Fixed line-heights keep the shield and text on one centre line on screen and in the PDF
const Verified = ({ email }) => (
  <div className="mx-auto mt-7 flex h-[40px] w-fit items-center gap-[10px] rounded-full border border-[#bbf7d0] bg-[#f0fdf4] px-5" data-testid="tw-cert-verified">
    <ShieldCheck className="block h-[20px] w-[20px] shrink-0 text-[#16a34a]" strokeWidth={2.25} />
    <span className="block text-[15px] font-semibold leading-[20px] text-[#166534]">Verified digital certificate</span>
    {email && (
      <>
        <span className="block h-[5px] w-[5px] shrink-0 rounded-full bg-[#86efac]" />
        <span className="block text-[15px] leading-[20px] text-[#475569]">Issued to <span className="font-semibold text-[#334155]">{email}</span></span>
      </>
    )}
  </div>
);

const Brand = () => (
  <>
    <img src="/tekwissen-emblem.png" alt="TekWissen Group" className="absolute left-[34px] top-[22px] h-[104px] w-[104px]" />
    <img src="/tekwissen-wordmark.png" alt="TekWissen - Empower your business" className="mx-auto h-[96px] w-auto pt-2" />
  </>
);

// The one TekWissen certificate used by every programme (fixed 1000px design; scale it with ScaledCertificate)
export const TekWissenCertificate = forwardRef(({ data }, ref) => (
  <div ref={ref} className="bg-white p-[14px] text-center" style={{ width: CERT_WIDTH, fontFamily: SANS }} data-testid="tw-certificate">
    <div className="rounded-[10px] border-[3px] bg-white p-[12px]" style={{ borderColor: TEAL }}>
      <div className="relative rounded-[4px] border border-[#e5e7eb] px-[64px] pb-6 pt-7">
        <Brand />
        <h1 className="mt-4 text-[54px] font-bold leading-tight text-[#1e293b]" style={{ fontFamily: SERIF }}>Certificate of Completion</h1>
        <p className="mt-3 text-[19px] uppercase tracking-[0.3em] text-[#1e293b]" data-testid="tw-cert-subtitle">{data.subtitle}</p>
        <p className="mt-14 text-[17px] text-[#334155]">This is to certify that</p>
        <p className="mx-auto mt-4 inline-block border-b-2 px-1 pb-2 text-[46px] uppercase leading-tight" style={{ fontFamily: SERIF, color: TEAL, borderColor: TEAL }} data-testid="tw-cert-recipient">
          {data.recipientName}
        </p>
        <Description parts={data.description} />
        <Stats stats={data.stats} />
        <Footer credentialId={data.credentialId} issuedAt={data.issuedAt} />
        <Verified email={data.recipientEmail} />
      </div>
    </div>
  </div>
));
TekWissenCertificate.displayName = 'TekWissenCertificate';
